import {api, driverContactRuleEndpoints, externalContactEndpoints, objectTypeEndpoints, resourceContactRuleGroupEndpoints, resourceEndpoints, tagEndpoints, userEndpoints} from '@/lib/api';

// Centralized mock data. Single source of truth for the frontend preview.
// Shared between FleetDashboard, UsersManagement, ContactRulesPage, etc.
// When the real backend is wired, these will be replaced by API responses.

// -------- Users (all system users, can have multiple roles) --------
const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const roleKey = (role) =>
    String(role?.name ?? role?.slug ?? role?.key ?? role?.id ?? role ?? '').trim().toLowerCase();

const userRoles = (user = {}) => {
    const roles = user.roles ?? user.role ?? user.profile ?? user.type ?? [];
    return Array.isArray(roles) ? roles.map(roleKey).filter(Boolean) : [roleKey(roles)].filter(Boolean);
};

const isDriverUser = (user = {}) => {
    const roles = userRoles(user);
    if (!roles.length) return true;
    return roles.includes('condutor') || roles.includes('driver');
};

const normalizeAvailableDriver = (user = {}, index = 0) => {
    const attributes = user.attributes && typeof user.attributes === 'object' && !Array.isArray(user.attributes)
        ? user.attributes
        : {};
    const merged = {...user, ...attributes};
    const firstName = merged.first_name ?? merged.firstName ?? '';
    const lastName = merged.last_name ?? merged.lastName ?? '';
    const derivedName = `${firstName} ${lastName}`.trim();

    return {
        id: user.id ?? merged.id ?? merged.user_id ?? merged.company_user_id ?? merged.driver_id ?? index + 1,
        name: merged.name ?? merged.full_name ?? merged.display_name ?? derivedName ?? merged.email ?? `Condutor ${index + 1}`,
        nickname: merged.nickname ?? merged.nick_name ?? '',
        email: merged.email ?? '',
        phone: merged.phone ?? merged.phone_number ?? merged.mobile ?? '',
    };
};

export const fetchUsers = async () => {
    const response = await api.get(userEndpoints.list);
    return collectionPayload(response.data, 'users');
};

export const fetchAvailableDrivers = async () => {
    const users = await fetchUsers();
    return users
        .filter(isDriverUser)
        .map(normalizeAvailableDriver)
        .filter(driver => driver.id && driver.name);
};

export const fetchRuleGroups = async () => {
    const response = await api.get(resourceContactRuleGroupEndpoints.list);
    const groups = collectionPayload(response.data, 'resource_contact_rule_groups');
    const contactGroups = groups.length ? groups : collectionPayload(response.data, 'contact_rule_groups');
    return contactGroups.length ? contactGroups : collectionPayload(response.data, 'groups');
};

export const FIELD_TYPES = {
    text: {label: 'Texto curto'},
    number: {label: 'Número'},
    date: {label: 'Data'},
    select: {label: 'Lista de opções'},
    textarea: {label: 'Texto longo'},
    coords: {label: 'Geolocalização (lat, lng)'},
};

const safeJson = (value, fallback) => {
    if (value === undefined || value === null || value === '') return fallback;
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
};

const arrayFrom = (value) => {
    const parsed = safeJson(value, value);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') return Object.values(parsed);
    return [];
};

const normalizeContactRuleGroupIds = (source = {}, fallback = {}) => {
    const directIds = arrayFrom(
        source.resource_contact_rule_group_ids
        ?? source.contact_rule_group_ids
        ?? source.contact_rules_group_ids
        ?? source.contactRuleGroupIds
        ?? []
    );
    const relationGroups = collectionPayload(source, 'resource_contact_rule_groups').length
        ? collectionPayload(source, 'resource_contact_rule_groups')
        : collectionPayload(source, 'contact_rule_groups');
    const relationIds = relationGroups.map(group => group?.id ?? group?.resource_contact_rule_group_id ?? group?.contact_rule_group_id).filter(Boolean);
    const legacyId = source.resource_contact_rule_group_id ?? source.contact_rule_group_id ?? source.contact_rules_group_id ?? fallback.contactRuleGroupId ?? null;
    const fallbackIds = arrayFrom(fallback.contactRuleGroupIds);
    const ids = directIds.length ? directIds : relationIds.length ? relationIds : fallbackIds.length ? fallbackIds : legacyId ? [legacyId] : [];

    return [...new Set(ids.map(id => String(id)).filter(Boolean))];
};

const normalizeFieldOptions = (options) =>
    arrayFrom(options).map((option, index) => {
        if (option && typeof option === 'object') {
            const value = option.value ?? option.option_value ?? option.key ?? option.code ?? option.name ?? option.label ?? option.id ?? index;
            return {
                value: String(value),
                label: option.label ?? option.name ?? option.title ?? String(value),
            };
        }
        return {value: String(option), label: String(option)};
    });

const normalizeInputType = (type = 'text') => {
    const normalized = String(type || 'text').toLowerCase();
    if (['select', 'dropdown', 'radio'].includes(normalized)) return 'select';
    if (['number', 'numeric', 'integer', 'decimal', 'float'].includes(normalized)) return 'number';
    if (['textarea', 'long_text', 'text_area'].includes(normalized)) return 'textarea';
    if (['coordinates', 'coordinate', 'location', 'coords'].includes(normalized)) return 'coords';
    if (['date', 'time', 'datetime-local', 'email', 'tel', 'url'].includes(normalized)) return normalized;
    return 'text';
};

const normalizeObjectTypeField = (field = {}, index = 0) => ({
    ...field,
    id: field.id ?? field.attribute_key ?? field.key ?? field.name ?? `field_${index + 1}`,
    attributeKey: field.attribute_key ?? field.key ?? field.name ?? `field_${index + 1}`,
    valuePath: field.value_path ?? (field.attribute_key ? `attributes.${field.attribute_key}` : field.key ? `attributes.${field.key}` : `attributes.field_${index + 1}`),
    label: field.label ?? field.name ?? field.title ?? field.attribute_key ?? `Campo ${index + 1}`,
    type: normalizeInputType(field.input_type ?? field.field_type ?? field.type),
    required: field.required === true || field.required === 1 || field.required === '1' || field.is_required === true || field.validation?.required === true,
    validation: field.validation ?? {},
    options: normalizeFieldOptions(field.options),
});

const normalizeObjectType = (source = {}, index = 0) => {
    const attributes = source.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? source.attributes
        : {};
    const merged = {...source, ...attributes};
    const id = source.id ?? merged.id ?? merged.object_type_id ?? merged.company_object_type_id ?? merged.uuid ?? index + 1;
    const fields = arrayFrom(merged.fields ?? merged.custom_fields ?? merged.schema ?? merged.form_fields)
        .map(normalizeObjectTypeField)
        .sort((a, b) => Number(a.order_no ?? 0) - Number(b.order_no ?? 0));

    return {
        ...source,
        ...attributes,
        id,
        name: merged.name ?? merged.title ?? merged.label ?? `Tipo ${index + 1}`,
        description: merged.description ?? '',
        color: merged.color ?? merged.hex_color ?? merged.object_color?.color ?? '#64748b',
        icon: merged.icon ?? merged.icon_name ?? merged.object_icon?.icon ?? 'Box',
        requiresDriver: merged.requires_driver === true || merged.requires_driver === 1 || merged.requires_driver === '1',
        fields,
        defaultImage: merged.default_image ?? merged.default_image_url ?? merged.image ?? null,
    };
};

let objectTypesCache = null;

export const fetchObjectTypes = async ({force = false} = {}) => {
    if (!force && objectTypesCache) return objectTypesCache;

    const response = await api.get(objectTypeEndpoints.list);
    const objectTypes = collectionPayload(response.data, 'object_types');
    const types = objectTypes.length ? objectTypes : collectionPayload(response.data, 'types');
    objectTypesCache = types.map(normalizeObjectType).filter(type => type.id && type.name);
    return objectTypesCache;
};

// Biblioteca de imagens stock que o utilizador pode escolher ao criar/editar um veículo/equipamento.
// Cada entrada tem um "kind" que mapeia mentalmente ao tipo de objeto (só para filtro visual).
export const IMAGE_LIBRARY = [
    {id: 'car-1', kind: 'Carro', url: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=600&auto=format&fit=crop&q=60', label: 'Sedan prateado'},
    {id: 'car-2', kind: 'Carro', url: 'https://images.unsplash.com/photo-1502877338535-766e1452684a?w=600&auto=format&fit=crop&q=60', label: 'SUV branco'},
    {id: 'car-3', kind: 'Carro', url: 'https://images.unsplash.com/photo-1542362567-b07e54358753?w=600&auto=format&fit=crop&q=60', label: 'Hatchback azul'},
    {id: 'car-4', kind: 'Carro', url: 'https://images.unsplash.com/photo-1617788138017-80ad40651399?w=600&auto=format&fit=crop&q=60', label: 'Tesla branco'},
    {id: 'bike-1', kind: 'Mota', url: 'https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=600&auto=format&fit=crop&q=60', label: 'Mota desportiva'},
    {id: 'bike-2', kind: 'Mota', url: 'https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=600&auto=format&fit=crop&q=60', label: 'Scooter urbana'},
    {id: 'truck-1', kind: 'Camião', url: 'https://images.unsplash.com/photo-1586191582056-b5d6147b2f4d?w=600&auto=format&fit=crop&q=60', label: 'Camião de carga'},
    {id: 'truck-2', kind: 'Camião', url: 'https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=600&auto=format&fit=crop&q=60', label: 'Carrinha de entregas'},
    {id: 'machine-1', kind: 'Máquina', url: 'https://images.unsplash.com/photo-1517524008697-84bbe3c3fd98?w=600&auto=format&fit=crop&q=60', label: 'Empilhador'},
    {id: 'charger-1', kind: 'Carregador Elétrico', url: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=600&auto=format&fit=crop&q=60', label: 'Posto carregamento'},
    {id: 'charger-2', kind: 'Carregador Elétrico', url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&auto=format&fit=crop&q=60', label: 'Estação rápida'},
    {id: 'street-1', kind: 'Mobiliário Urbano', url: 'https://images.unsplash.com/photo-1519642918688-7e43b19245d8?w=600&auto=format&fit=crop&q=60', label: 'Banco de jardim'},
];


const normalizeAuthorizedReporter = (contact = {}, index = 0) => ({
    id: contact.id ?? contact.external_contact_id ?? contact.contact_id ?? index + 1,
    uuid: contact.uuid,
    name: contact.name ?? `Alertante ${index + 1}`,
    nickname: contact.nickname ?? contact.nick_name ?? '',
    phone: contact.phone ?? contact.phone_number ?? '',
    email: contact.email ?? '',
    organization: contact.organization ?? contact.organisation ?? contact.company ?? '',
    externalContactTypeId: String(contact.external_contact_type_id ?? contact.contact_type_id ?? contact.contactTypeId ?? contact.contact_type ?? contact.contact_type_key ?? contact.contactType ?? contact.category ?? ''),
});

export const fetchAuthorizedReporters = async () => {
    const response = await api.get(externalContactEndpoints.list);
    const contacts = collectionPayload(response.data, 'contacts');
    const externalContacts = contacts.length ? contacts : collectionPayload(response.data, 'external_contacts');
    return externalContacts
        .map(normalizeAuthorizedReporter)
        .filter(contact => contact.id && contact.name);
};

export const getDefaultImageForObjectType = async (objectTypeId) => {
    const objectTypes = await fetchObjectTypes();
    const t = objectTypes.find(x => String(x.id) === String(objectTypeId));
    return t?.defaultImage || null;
};

export const getObjectType = async (id) => {
    const objectTypes = await fetchObjectTypes();
    return objectTypes.find(t => String(t.id) === String(id)) || null;
};

export const DAYS = [
    {key: 'mon', short: 'Seg', label: 'Segunda'},
    {key: 'tue', short: 'Ter', label: 'Terça'},
    {key: 'wed', short: 'Qua', label: 'Quarta'},
    {key: 'thu', short: 'Qui', label: 'Quinta'},
    {key: 'fri', short: 'Sex', label: 'Sexta'},
    {key: 'sat', short: 'Sáb', label: 'Sábado'},
    {key: 'sun', short: 'Dom', label: 'Domingo'},
];

const DAY_ID_TO_KEY = {
    1: 'mon',
    2: 'tue',
    3: 'wed',
    4: 'thu',
    5: 'fri',
    6: 'sat',
    7: 'sun',
};

const driverContactChannelFallback = (channel) => ({
    enabled: channel !== 'call',
    mode: 'uniform',
    days: ['mon', 'tue', 'wed', 'thu', 'fri'],
    start: '08:00',
    end: '18:00',
    perDay: {},
});

const normalizeDriverChannelKey = (source) => {
    const value = source && typeof source === 'object'
        ? source.channel ?? source.type ?? source.name ?? source.channel_name ?? source.channel_id ?? source.id
        : source;
    const normalized = String(value ?? '').trim().toLowerCase();
    if (['sms', '1'].includes(normalized)) return 'sms';
    if (['email', 'mail', 'e-mail', '2'].includes(normalized)) return 'email';
    if (['call', 'phone', 'voice', 'chamada', '3'].includes(normalized)) return 'call';
    return normalized;
};

const normalizeDriverChannelDayKey = (day) => {
    const dayId = typeof day === 'object' ? day.day_id ?? day.id : day;
    return DAY_ID_TO_KEY[Number(dayId)] || day?.key || day?.day || day;
};

const timeToHourMinute = (value, fallback = '') => {
    if (!value) return fallback;
    const match = String(value).match(/^(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : String(value);
};

const normalizeDriverChannelEnabled = (source = {}, fallback = true) => {
    if (source.enabled !== undefined) return Boolean(source.enabled);
    if (source.status !== undefined) return Number(source.status) === 1 || source.status === true;
    return fallback;
};

const normalizeDriverContactChannel = (channel, source = {}) => {
    source = source || {};
    const fallback = driverContactChannelFallback(channel);
    const daySource = source.days ?? source.channel_days ?? source.day_ids ?? fallback.days;
    const perDaySource = source.perDay ?? source.per_day ?? source.channel_per_day ?? source.per_days ?? {};
    const days = Array.isArray(daySource)
        ? daySource.map(normalizeDriverChannelDayKey).filter(Boolean)
        : fallback.days;
    const perDay = Array.isArray(perDaySource)
        ? perDaySource.reduce((acc, item) => {
            const key = normalizeDriverChannelDayKey(item);
            if (!key) return acc;
            acc[key] = {
                enabled: normalizeDriverChannelEnabled(item),
                start: timeToHourMinute(item.start_time_at ?? item.start, fallback.start),
                end: timeToHourMinute(item.end_time_at ?? item.end, fallback.end),
            };
            return acc;
        }, {})
        : (perDaySource || {});

    return {
        ...fallback,
        ...source,
        enabled: normalizeDriverChannelEnabled(source, fallback.enabled),
        mode: source.mode ?? fallback.mode,
        days,
        start: timeToHourMinute(source.uniform_start_at ?? source.start_time_at ?? source.start, fallback.start),
        end: timeToHourMinute(source.uniform_end_at ?? source.end_time_at ?? source.end, fallback.end),
        perDay,
    };
};

const normalizeDriverContactChannels = (source) => {
    const parsed = typeof source === 'string' ? (() => {
        try {
            return JSON.parse(source);
        } catch {
            return {};
        }
    })() : (source || {});

    const channelSource = Array.isArray(parsed)
        ? parsed.reduce((acc, item) => {
            const key = normalizeDriverChannelKey(item);
            if (key) acc[key] = item;
            return acc;
        }, {})
        : {
            ...parsed,
            sms: parsed.sms ?? parsed.sms_channel,
            email: parsed.email ?? parsed.email_channel,
            call: parsed.call ?? parsed.call_channel ?? parsed.phone_channel,
        };

    return {
        sms: normalizeDriverContactChannel('sms', channelSource.sms),
        email: normalizeDriverContactChannel('email', channelSource.email),
        call: normalizeDriverContactChannel('call', channelSource.call),
    };
};

export const normalizeDriverContactRule = (rule = {}, fallback = {}) => ({
    ...fallback,
    ...rule,
    id: rule.id ?? rule.manager_contact_rule_id ?? fallback.id,
    uuid: rule.uuid ?? fallback.uuid,
    name: rule.name ?? fallback.name ?? '',
    description: rule.description ?? fallback.description ?? '',
    scope: rule.scope ?? rule.type ?? fallback.scope ?? 'category',
    category: rule.category ?? rule.department?.name ?? rule.department_name ?? rule.department ?? fallback.category ?? null,
    department_id: rule.department_id ?? fallback.department_id ?? null,
    channels: normalizeDriverContactChannels(rule.channels ?? rule.contact_channels ?? fallback.channels),
});

export const normalizeDriverContactRules = (payload) => {
    const rules = collectionPayload(payload, 'rules');
    const managerRules = rules.length ? rules : collectionPayload(payload, 'manager_contact_rules');
    return managerRules.map((rule, index) => normalizeDriverContactRule(rule, {id: index + 1}));
};

export const fetchDriverContactRules = async () => {
    const response = await api.get(driverContactRuleEndpoints.list);
    return normalizeDriverContactRules(response.data);
};

export const resolveDriverRuleForVehicle = (vehicle, rules = []) => {
    if (!vehicle) return rules.find(r => r.scope === 'general') || null;
    if (vehicle.driverContactRuleId) {
        const r = rules.find(x => x.id === vehicle.driverContactRuleId);
        if (r) return r;
    }
    const byCat = rules.find(r => r.scope === 'category' && r.category === vehicle.category);
    if (byCat) return byCat;
    return rules.find(r => r.scope === 'general') || null;
};

const normalizeResourceDriver = (source = null) => {
    if (!source) return null;
    const attributes = source.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? source.attributes
        : {};
    const merged = {...source, ...attributes};
    const firstName = merged.first_name ?? merged.firstName ?? '';
    const lastName = merged.last_name ?? merged.lastName ?? '';
    const derivedName = `${firstName} ${lastName}`.trim();

    return {
        id: source.id ?? merged.id ?? merged.user_id ?? merged.company_user_id ?? merged.driver_id,
        name: merged.name ?? merged.full_name ?? merged.display_name ?? derivedName ?? merged.email ?? '',
        nickname: merged.nickname ?? merged.nick_name ?? '',
        email: merged.email ?? '',
        phone: merged.phone ?? merged.phone_number ?? merged.mobile ?? '',
    };
};

const normalizeResourceTag = (source = null) => {
    if (!source) return null;
    return {
        id: source.id ?? source.tag_id ?? source.code,
        location: source.location ?? source.location_name ?? '',
        status: source.status ?? 'active',
    };
};

const parseCoordinateNumber = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const normalized = typeof value === 'string' ? value.trim().replace(',', '.') : value;
    const number = Number(normalized);
    return Number.isFinite(number) ? number : null;
};

const coordinatePairFrom = (value) => {
    const parsed = safeJson(value, value);

    if (Array.isArray(parsed) && parsed.length >= 2) {
        const lat = parseCoordinateNumber(parsed[0]);
        const lng = parseCoordinateNumber(parsed[1]);
        return lat !== null && lng !== null ? {lat, lng} : null;
    }

    if (parsed && typeof parsed === 'object') {
        const lat = parseCoordinateNumber(
            parsed.lat
            ?? parsed.latitude
            ?? parsed.gps_latitude
            ?? parsed.location_latitude
        );
        const lng = parseCoordinateNumber(
            parsed.lng
            ?? parsed.lon
            ?? parsed.long
            ?? parsed.longitude
            ?? parsed.gps_longitude
            ?? parsed.location_longitude
        );
        return lat !== null && lng !== null ? {lat, lng} : null;
    }

    if (typeof parsed === 'string') {
        const match = parsed.match(/(-?\d+(?:[.,]\d+)?)\s*[,;]\s*(-?\d+(?:[.,]\d+)?)/);
        if (match) {
            const lat = parseCoordinateNumber(match[1]);
            const lng = parseCoordinateNumber(match[2]);
            return lat !== null && lng !== null ? {lat, lng} : null;
        }
    }

    return null;
};

const normalizeResourceCoords = (source = {}, customFields = {}) => {
    const directLat = parseCoordinateNumber(
        source.latitude
        ?? source.lat
        ?? source.gps_latitude
        ?? source.gps_lat
        ?? source.location_latitude
        ?? source.coord_lat
        ?? source.coordinate_latitude
        ?? customFields.latitude
        ?? customFields.lat
        ?? customFields.gps_latitude
        ?? customFields.location_latitude
    );
    const directLng = parseCoordinateNumber(
        source.longitude
        ?? source.lng
        ?? source.lon
        ?? source.long
        ?? source.gps_longitude
        ?? source.gps_lng
        ?? source.location_longitude
        ?? source.coord_lng
        ?? source.coordinate_longitude
        ?? customFields.longitude
        ?? customFields.lng
        ?? customFields.lon
        ?? customFields.gps_longitude
        ?? customFields.location_longitude
    );

    if (directLat !== null && directLng !== null) return {lat: directLat, lng: directLng};

    const coordinateSources = [
        source.coords,
        source.coordinates,
        source.coordinate,
        source.geolocation,
        source.geo_location,
        source.gps,
        source.position,
        source.last_position,
        customFields.coords,
        customFields.coordinates,
        customFields.coordinate,
        customFields.geolocation,
        customFields.geo_location,
        customFields.gps,
        customFields.position,
    ];

    for (const candidate of coordinateSources) {
        const coords = coordinatePairFrom(candidate);
        if (coords) return coords;
    }

    return null;
};

const omitLegacyVehicleFields = (source = {}) => {
    const clean = {...source};
    delete clean.plate;
    delete clean.license_plate;
    delete clean.model;
    delete clean.year;
    return clean;
};

export const normalizeResource = (source = {}, fallback = {}) => {
    const resourceSource = omitLegacyVehicleFields({...fallback, ...source});
    const customFields = safeJson(source.attributes ?? source.custom_fields ?? source.fields ?? source.metadata, {});
    const objectType = source.object_type ?? source.type ?? {};
    const companyManager = normalizeResourceDriver(source.company_manager ?? source.manager);
    const companyDepartment = source.company_department ?? source.department ?? {};
    const id = source.id ?? source.resource_id ?? fallback.id;
    const reference = source.reference ?? customFields.reference ?? fallback.reference ?? '';
    const resourceName = source.name ?? source.title ?? customFields.name ?? customFields.description ?? fallback.name ?? fallback.resourceName ?? '';
    const resourceType = source.resource_type ?? source.resourceType ?? (typeof source.type === 'string' ? source.type : null) ?? customFields.type ?? fallback.resourceType ?? '';
    const contactRuleGroupIds = normalizeContactRuleGroupIds(source, fallback);

    return {
        ...resourceSource,
        id,
        uuid: source.uuid ?? fallback.uuid,
        reference: String(reference || resourceName || `RES-${id || ''}`).trim(),
        code: source.code ?? customFields.code ?? fallback.code ?? '',
        name: String(resourceName || reference || `Recurso ${id || ''}`).trim(),
        resourceName: String(resourceName || reference || `Recurso ${id || ''}`).trim(),
        resourceType: String(resourceType ?? '').trim(),
        type: source.resource_type ?? source.type_name ?? objectType.name ?? objectType.title ?? fallback.type ?? 'resource',
        typeId: source.object_type_id ?? objectType.id ?? fallback.typeId ?? null,
        objectTypeId: source.object_type_id ?? objectType.id ?? fallback.objectTypeId ?? fallback.typeId ?? null,
        typeName: objectType.name ?? objectType.title ?? source.type_name ?? fallback.typeName ?? '',
        typeColor: objectType.color ?? objectType.hex_color ?? fallback.typeColor ?? '#64748b',
        typeIcon: objectType.icon ?? objectType.icon_name ?? fallback.typeIcon ?? 'Box',
        image: source.image ?? source.image_url ?? source.photo_url ?? fallback.image ?? null,
        color: source.color ?? customFields.color ?? fallback.color ?? 'N/A',
        category: source.category ?? companyDepartment?.name ?? companyDepartment?.title ?? source.company_department_name ?? source.department_name ?? fallback.category ?? 'Sem categoria',
        departmentId: source.company_department_id ?? source.department_id ?? companyDepartment?.id ?? fallback.departmentId ?? null,
        status: source.status ?? fallback.status ?? 'active',
        resourceStatusId: source.resource_status_id ?? source.gest_resource_status_id ?? source.resource_status?.id ?? fallback.resourceStatusId ?? null,
        fleetManagerId: source.company_manager_id ?? source.fleet_manager_id ?? source.manager_id ?? fallback.fleetManagerId ?? null,
        fleetManager: companyManager ?? fallback.fleetManager ?? null,
        driver: normalizeResourceDriver(source.driver ?? source.user ?? source.assigned_user ?? source.company_manager ?? source.manager) ?? fallback.driver ?? null,
        tag: normalizeResourceTag(source.tag) ?? fallback.tag ?? null,
        contactRuleGroupIds,
        contactRuleGroupId: contactRuleGroupIds[0] ?? source.resource_contact_rule_group_id ?? source.contact_rule_group_id ?? source.contact_rules_group_id ?? fallback.contactRuleGroupId ?? null,
        driverContactRuleId: source.driver_contact_rule_id ?? source.manager_contact_rule_id ?? fallback.driverContactRuleId ?? null,
        lastActivity: source.last_activity ?? source.updated_at ?? fallback.lastActivity ?? '',
        location: source.location ?? source.location_name ?? customFields.location ?? customFields.location_name ?? fallback.location ?? '',
        coords: normalizeResourceCoords(source, customFields) ?? fallback.coords ?? null,
        customFields,
    };
};

export const normalizeResources = (payload) => {
    const resources = collectionPayload(payload, 'resources');
    const items = resources.length ? resources : collectionPayload(payload, 'vehicles');
    return items.map(normalizeResource).filter(resource => resource.id);
};

export let mockVehicles = [];

export const fetchVehicles = async () => {
    const response = await api.get(resourceEndpoints.list);
    mockVehicles = normalizeResources(response.data);
    return mockVehicles;
};

// -------- Chargers --------
// Type='carregador'. Identified by its resource reference for uniqueness.
export const mockChargers = [];

// -------- Other fleet objects (empilhadores, caixas de frio, etc.) --------
export const mockOtherObjects = [];

// -------- Tags --------
const unwrapTagAttributes = (source = {}) =>
    source?.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? {...source, ...source.attributes}
        : source;

const normalizeAvailableTagStatus = (tag = {}, resource = {}) => {
    if (tag.deactivated_at || tag.gest_tag_deactivation_reason_id || tag.deactivate_reason_id || tag.deactivation_reason_id) return 'inativa';
    if (tag.gest_resource_id || tag.resource_id || resource?.id) return 'atribuida';

    const value = String(tag.status ?? '').toLowerCase();
    const map = {
        0: 'armazem',
        1: 'atribuida',
        2: 'inativa',
        available: 'armazem',
        warehouse: 'armazem',
        armazem: 'armazem',
        active: 'ativa',
        ativa: 'ativa',
        assigned: 'atribuida',
        atribuida: 'atribuida',
        inactive: 'inativa',
        inativa: 'inativa',
    };
    return map[value] || 'armazem';
};

const normalizeAvailableTag = (source = {}, index = 0) => {
    const tag = unwrapTagAttributes(source);
    const batch = unwrapTagAttributes(tag.gest_tag_batch ?? tag.tag_batch ?? tag.batch ?? {});
    const resource = unwrapTagAttributes(tag.gest_resource ?? tag.resource ?? {});
    const objectType = unwrapTagAttributes(batch.object_type ?? tag.object_type ?? {});
    const reference = tag.reference ?? tag.serial_no ?? tag.code ?? source.id ?? tag.id ?? tag.uuid ?? `TAG-${index + 1}`;

    return {
        id: String(reference),
        tagId: source.id ?? tag.id ?? null,
        uuid: tag.uuid,
        assignedDate: String(tag.created_at ?? tag.delivered_at ?? batch.delivered_at ?? batch.created_at ?? '').slice(0, 10),
        category: objectType.name ?? objectType.title ?? batch.object_type_name ?? tag.category ?? 'Sem tipo de objeto',
        status: normalizeAvailableTagStatus(tag, resource),
    };
};

export const normalizeAvailableTags = (payload) =>
    collectionPayload(payload, 'tags')
        .map(normalizeAvailableTag)
        .filter(tag => tag.id && ['armazem', 'ativa'].includes(tag.status));

export const fetchAvailableTags = async () => {
    const response = await api.get(tagEndpoints.list);
    return normalizeAvailableTags(response.data);
};

const searchableText = (...parts) =>
    parts
        .filter(Boolean)
        .join(' ')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

const geoResourceType = (resource = {}) => {
    const text = searchableText(resource.type, resource.typeName, resource.resource_type, resource.category);
    if (/(carregador|charger|charging|evse|posto)/.test(text)) return 'carregador';
    if (/(veiculo|vehicle|viatura|carro|mota|moto|truck|camiao|carrinha|van)/.test(text)) return 'veiculo';
    return 'outro';
};

const normalizeGeoLocatedResource = (resource = {}) => {
    if (!resource.coords) return null;

    const type = geoResourceType(resource);
    const identifier = String(
        resource.reference
        || resource.code
        || resource.uuid
        || resource.id
        || ''
    ).trim();

    if (!identifier) return null;

    return {
        uid: `${type}:${identifier}`,
        type,
        identifier,
        label: resource.name || resource.reference || identifier,
        status: resource.status,
        driver: resource.driver?.name,
        coords: resource.coords,
        category: resource.category,
        extra: resource.location,
    };
};

export const getGeoLocatedObjects = async () => {
    const response = await api.get(resourceEndpoints.list);
    const resources = normalizeResources(response.data);
    mockVehicles = resources.filter(resource => geoResourceType(resource) === 'veiculo');
    return resources.map(normalizeGeoLocatedResource).filter(Boolean);
};

// -------- Derived helpers --------
/**
 * Returns the list of registered vehicles as `{reference, name}` â€” used by
 * pickers that specifically need vehicles (e.g., assigning a vehicle to a
 * user/driver). Derived from mockVehicles so there's one source of truth.
 */
export const getRegisteredVehicles = (vehicles = mockVehicles) =>
    vehicles.map(v => ({reference: v.reference, name: v.name}));

/**
 * Returns vehicle/rule-group assignment pairs used by ContactRulesPage
 * to show which vehicles use each rule group.
 */
export const getVehicleAssignments = (vehicles = mockVehicles) =>
    vehicles.flatMap(v => {
        const groupIds = v.contactRuleGroupIds?.length
            ? v.contactRuleGroupIds
            : v.contactRuleGroupId
                ? [v.contactRuleGroupId]
                : [];

        return groupIds.map(ruleGroupId => ({
        reference: v.reference,
        name: v.name,
        ruleGroupId,
        ruleGroupIds: groupIds,
        hasDriver: !!v.driver,
        }));
    });

/**
 * Returns all fleet objects (vehicles + chargers + others) in a unified
 * shape for generic pickers â€” e.g., "Atribuir Tag a Objeto". Each item:
 *   { uid, type, identifier, label, category, hasTag }
 * `uid` is a unique string composed of `type:identifier` to avoid
 * collisions between resource types.
 */
export const TYPE_LABELS = {
    veiculo: 'Veículo',
    carregador: 'Carregador',
    outro: 'Outro',
};

export const getAllFleetObjects = (
    vehicles = mockVehicles,
    chargers = mockChargers,
    others = mockOtherObjects,
) => {
    const toEntry = (o) => {
        const identifier = o.reference || o.code;
        return {
            uid: `${o.type}:${identifier}`,
            type: o.type,
            identifier,
            label: o.name,
            category: o.category,
            hasTag: !!o.tag,
        };
    };
    return [...vehicles.map(toEntry), ...chargers.map(toEntry), ...others.map(toEntry)];
};

/**
 * Counts fleet objects (vehicles + chargers + others) whose `objectTypeId`
 * matches one of the given `objectTypeIds`. Used by IncidentTypesConfig to
 * show "X elegíveis" â€” i.e., how many real fleet objects could trigger this
 * type of incident report.
 */
export const countEligibleObjectsForType = (
    objectTypeIds = [],
    {vehicles = mockVehicles, chargers = mockChargers, others = mockOtherObjects} = {},
) => {
    if (!objectTypeIds || objectTypeIds.length === 0) return 0;
    const ids = new Set(objectTypeIds.map(Number));
    const all = [...vehicles, ...chargers, ...others];
    return all.filter(o => o.objectTypeId != null && ids.has(Number(o.objectTypeId))).length;
};

